<template>
  <div class="payments-page h-full overflow-y-auto p-4">
    <header class="flex flex-wrap items-center justify-between gap-3 mb-4">
      <h1 class="text-2xl font-bold">Pagos</h1>
      <button class="rounded bg-primary hover:bg-primary-dark text-white font-bold py-2 px-4 disabled:opacity-50" :disabled="!canDownload" @click="download">Exportar CSV</button>
    </header>

    <div class="payments-filters">
      <label class="payments-period" for="payment-period">Período
        <select id="payment-period" :value="selectedPreset" @change="applyPreset($event.target.value)">
          <option value="month">Este mes</option>
          <option value="last_month">Mes anterior</option>
          <option value="custom">Rango personalizado</option>
        </select>
      </label>
      <label for="payment-from">Desde
        <input id="payment-from" :value="from" type="date" @change="updateDate('from', $event.target.value)" />
      </label>
      <label for="payment-to">Hasta
        <input id="payment-to" :value="to" type="date" @change="updateDate('to', $event.target.value)" />
      </label>
    </div>
    <p v-if="invalidDates" class="text-sm text-red mb-4" role="alert">{{ error }}</p>

    <div class="payments-table-wrap border border-gray-200 rounded bg-white overflow-x-auto" :aria-busy="loading">
      <p v-if="loading" class="sr-only" role="status">Cargando pagos…</p>
      <table class="w-full text-sm text-left" aria-label="Órdenes pagadas con tarjeta">
        <thead class="border-b border-gray-200">
          <tr>
            <th scope="col">Orden</th>
            <th scope="col">Cliente</th>
            <th scope="col">Fecha de pago</th>
            <th scope="col" class="payments-number">Cobrado</th>
            <th scope="col">Estado</th>
          </tr>
        </thead>
        <tbody v-if="loading">
          <tr v-for="index in 6" :key="index" aria-hidden="true">
            <td class="payments-cell-order"><span class="payments-skeleton payments-skeleton-short"></span></td>
            <td class="payments-cell-client"><span class="payments-skeleton"></span></td>
            <td class="payments-cell-date"><span class="payments-skeleton"></span></td>
            <td class="payments-cell-amount"><span class="payments-skeleton payments-skeleton-short"></span></td>
            <td class="payments-cell-status"><span class="payments-skeleton"></span></td>
          </tr>
        </tbody>
        <tbody v-else-if="error && !invalidDates">
          <tr>
            <td colspan="5" class="payments-notice">
              <div role="alert"><strong>No pudimos cargar los pagos</strong><p>{{ error }}</p></div>
              <button class="border border-primary text-primary rounded font-bold py-2 px-4" @click="loadPayments">Volver a intentar</button>
            </td>
          </tr>
        </tbody>
        <tbody v-else-if="complete && !dirtyDates && rows.length">
          <tr v-for="row in pagedRows" :key="row.key">
            <td class="payments-cell-order">
              <router-link class="payments-order underline font-medium" :to="`/proveedor/${providerId}/orden/${row.order_id}`">#{{ row.order_id }}</router-link>
              <p v-if="row.cancelled" class="payments-note">Orden cancelada</p>
            </td>
            <td class="payments-cell-client">{{ row.client_name }}</td>
            <td class="payments-cell-date">{{ formatTime(row.paid_at) }}
              <p v-if="row.charges.length > 1" class="payments-note">{{ row.charges.length }} cobros · fecha del último</p>
            </td>
            <td class="payments-number payments-cell-amount">{{ formatMoney(row.amount, row.currency) }}</td>
            <td class="payments-cell-status"><span class="block rounded-full text-center py-1 px-2" :class="statusClass(row)">{{ paymentStatus(row) }}</span></td>
          </tr>
        </tbody>
        <tbody v-else-if="complete && !dirtyDates">
          <tr>
            <td colspan="5" class="payments-notice" role="status"><strong>No hay pagos en este período</strong><p>Prueba con otras fechas.</p></td>
          </tr>
        </tbody>
      </table>
    </div>
    <nav v-if="complete && !dirtyDates && pages > 1" class="flex justify-end items-center gap-3 mt-4 text-sm" aria-label="Páginas de pagos">
      <button class="border border-gray-300 rounded py-1 px-3 disabled:opacity-50" :disabled="page === 1" @click="page -= 1">Anterior</button>
      <span aria-live="polite">{{ page }} / {{ pages }}</span>
      <button class="border border-gray-300 rounded py-1 px-3 disabled:opacity-50" :disabled="page === pages" @click="page += 1">Siguiente</button>
    </nav>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import axios from "axios";
import { store } from "../store.js";
import {
  cardPaymentsCsv, datePreset, formatPaymentMoney, formatPaymentTime,
  groupCardPayments, paymentDayStart, paymentStatus,
} from "../shared/cardPayments.js";

export default {
  setup() {
    const route = useRoute();
    const router = useRouter();
    const providerId = computed(() => String(route.params.proveedor_id || ""));
    const initial = route.query.all === "true" ? datePreset("all") : datePreset("month");
    const textQuery = (key, fallback = "") => typeof route.query[key] === "string" ? route.query[key] : fallback;
    const from = ref(textQuery("from", initial.from));
    const to = ref(textQuery("to", initial.to));
    const matchingPreset = ["month", "last_month"].find((key) => {
      const range = datePreset(key);
      return from.value === range.from && to.value === range.to;
    });
    const selectedPreset = ref(textQuery("period") === "custom" ? "custom" : matchingPreset || "custom");
    const payments = ref([]);
    const complete = ref(false);
    const loading = ref(false);
    const error = ref("");
    const invalidDates = ref(false);
    const loadedRange = ref(null);
    const page = ref(1);
    const pageSize = 25;
    let run = 0;
    let controller;

    const dirtyDates = computed(() => loadedRange.value &&
      (from.value !== loadedRange.value.from || to.value !== loadedRange.value.to));
    const confirmed = computed(() => payments.value.filter((payment) => payment.order_id && !payment.review_reason));
    const rows = computed(() => groupCardPayments(confirmed.value));
    const pages = computed(() => Math.max(1, Math.ceil(rows.value.length / pageSize)));
    const pagedRows = computed(() => rows.value.slice((page.value - 1) * pageSize, page.value * pageSize));
    const canDownload = computed(() => complete.value && !loading.value && !dirtyDates.value && rows.value.length > 0);
    watch(payments, () => { page.value = 1; });

    async function loadPayments() {
      controller?.abort();
      const currentRun = ++run;
      loading.value = false;
      complete.value = false;
      error.value = "";
      invalidDates.value = false;
      payments.value = [];
      const ownerId = providerId.value;
      const range = { from: from.value, to: to.value };
      try {
        if (range.from) paymentDayStart(range.from);
        if (range.to) paymentDayStart(range.to);
        if (range.from && range.to && range.from > range.to) throw new Error("Desde debe ser anterior o igual a Hasta.");
      } catch (validationError) {
        error.value = validationError.message;
        invalidDates.value = true;
        return;
      }
      if (!store.auth || !/^[1-9]\d*$/.test(ownerId)) return;
      loading.value = true;
      loadedRange.value = range;
      controller = new AbortController();
      const signal = controller.signal;
      let after;
      let snapshot;
      const seenCursors = new Set();
      const found = new Map();
      try {
        await router.replace({ query: {
          ...(range.from || range.to ? { from: range.from, to: range.to } : { all: "true" }),
          period: selectedPreset.value,
        } });
        if (currentRun !== run) return;
        do {
          const { data } = await axios.get("/api/provider-card-payments", {
            params: { proveedor_id: ownerId, ...range, after, snapshot }, signal, timeout: 30000,
          });
          if (currentRun !== run) return;
          for (const payment of data.payments) found.set(payment.charge_id, payment);
          payments.value = [...found.values()];
          snapshot = data.snapshot;
          after = data.next;
          if (after && seenCursors.has(after)) throw new Error("No se pudo completar la consulta. Intenta de nuevo.");
          if (after) seenCursors.add(after);
        } while (after);
        complete.value = true;
      } catch (requestError) {
        if (currentRun !== run) return;
        if (requestError.response?.status === 401) store.setAuth(false);
        error.value = requestError.response?.data?.error ||
          (requestError.code === "ECONNABORTED" ? "La consulta tardó demasiado. Intenta de nuevo." : "Intenta de nuevo en unos momentos.");
      } finally {
        if (currentRun === run) loading.value = false;
      }
    }

    function applyPreset(key) {
      selectedPreset.value = key;
      if (key === "custom") return;
      const range = datePreset(key);
      from.value = range.from;
      to.value = range.to;
      return loadPayments();
    }

    function updateDate(field, value) {
      selectedPreset.value = "custom";
      if (field === "from") from.value = value;
      else to.value = value;
      return loadPayments();
    }

    function download() {
      if (!canDownload.value) return;
      const url = URL.createObjectURL(new Blob([cardPaymentsCsv(rows.value)], { type: "text/csv;charset=utf-8;" }));
      const anchor = document.createElement("a");
      const range = loadedRange.value;
      anchor.href = url;
      anchor.download = `pagos-tarjeta-${providerId.value}-ordenes-${range.from || 'inicio'}-${range.to || 'hoy'}.csv`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }

    function statusClass(row) {
      if (row.disputed) return "border border-yellow-500 text-yellow-500";
      if (row.amount > 0 && row.refunded >= row.amount) return "border border-gray-400 text-gray-500";
      return row.refunded > 0 ? "border border-yellow-500 text-yellow-500" : "border border-primary text-primary";
    }

    watch([() => store.auth, providerId], ([authorized]) => {
      if (authorized) loadPayments();
      else {
        run += 1;
        controller?.abort();
        payments.value = [];
        loading.value = false;
        complete.value = false;
      }
    }, { immediate: true, flush: "sync" });
    onBeforeUnmount(() => { run += 1; controller?.abort(); });

    return {
      from, to, providerId, selectedPreset, payments, loading, complete,
      error, invalidDates, dirtyDates, rows, canDownload,
      page, pages, pagedRows, loadPayments, applyPreset, updateDate, download, statusClass,
      formatMoney: formatPaymentMoney, formatTime: formatPaymentTime, paymentStatus,
    };
  },
};
</script>

<style scoped>
.payments-filters { display: grid; grid-template-columns: minmax(0, 220px) repeat(2, minmax(0, 175px)); gap: 12px; align-items: end; margin-bottom: 16px; }
.payments-filters label { @apply block text-sm font-bold text-gray-700; min-width: 0; }
.payments-filters input, .payments-filters select { @apply mt-1 border border-gray-300 rounded w-full py-2 px-3 text-gray-700 font-normal; min-width: 0; height: 42px; }
.payments-table-wrap th, .payments-table-wrap td { @apply py-3 px-6; }
.payments-table-wrap tbody tr { @apply border-b border-gray-200; }
.payments-table-wrap tbody tr:last-child { border-bottom: 0; }
.payments-cell-date { white-space: nowrap; }
.payments-number { white-space: nowrap; text-align: right; font-variant-numeric: tabular-nums; }
.payments-cell-status { min-width: 150px; }
.payments-note { @apply text-xs text-gray-500 mt-1; }
.payments-skeleton { @apply bg-gray-200 rounded; display: block; width: 100%; min-width: 60px; max-width: 110px; height: 12px; margin: 4px 0; }
.payments-skeleton-short { min-width: 40px; max-width: 55px; }
.payments-table-wrap td.payments-notice { @apply p-8 text-center; }
.payments-notice p { @apply mt-2 mb-4 text-sm text-gray-500; }
@media (min-width: 640px) { .payments-table-wrap table { min-width: 650px; } }
@media (max-width: 900px) {
  .payments-filters { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .payments-period { grid-column: 1 / -1; }
}
@media (max-width: 639px) {
  .payments-table-wrap thead { display: none; }
  .payments-table-wrap tbody { display: block; }
  .payments-table-wrap tr { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 4px 12px; padding: 12px; }
  .payments-table-wrap td { padding: 0; }
  .payments-cell-order { grid-column: 1; grid-row: 1; }
  .payments-cell-client { grid-column: 1; grid-row: 2; overflow-wrap: anywhere; }
  .payments-cell-date { grid-column: 1; grid-row: 3; white-space: normal; }
  .payments-cell-amount { grid-column: 2; grid-row: 1; }
  .payments-cell-status { grid-column: 2; grid-row: 2 / 4; min-width: 0; }
  .payments-table-wrap td.payments-notice { grid-column: 1 / -1; padding: 24px 0; }
  .payments-cell-amount .payments-skeleton, .payments-cell-status .payments-skeleton { margin-left: auto; }
}
@media (max-width: 380px) { .payments-filters { grid-template-columns: minmax(0, 1fr); } }
@media (pointer: coarse) {
  .payments-page button, .payments-filters input, .payments-filters select { min-height: 44px; }
  .payments-filters input, .payments-filters select { font-size: 16px; }
  .payments-order { display: inline-flex; align-items: center; min-height: 44px; }
}
</style>
